import React, {useMemo} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {openCookiePreferencesCenter} from '@vio/shared/src/search/cookies/utils'
import {getLanguageCode} from 'modules/meta/selectors'

import {Brand} from '@daedalus/core/src/brand/types'
import {
  checkIsCookieConsentEnabledForBrand,
  getLocalizedContactUrl,
  getLocalizedHelpUrl
} from '@daedalus/core/src/brand/utils'

const messages = defineMessages({
  productTitle: {
    id: 'product',
    defaultMessage: 'Product'
  },
  companyTitle: {
    id: 'company',
    defaultMessage: 'Company'
  },
  contactTitle: {
    id: 'contact',
    defaultMessage: 'Contact'
  },
  affiliateProgram: {
    id: 'affiliateProgram',
    defaultMessage: 'Affiliate program'
  },
  cookies: {
    id: 'cookies',
    defaultMessage: 'Cookies'
  },
  privacy: {
    id: 'privacy',
    defaultMessage: 'Privacy'
  },
  terms: {
    id: 'terms',
    defaultMessage: 'Terms'
  },
  aboutUs: {
    id: 'aboutUs',
    defaultMessage: 'About us'
  },
  blog: {
    id: 'blog',
    defaultMessage: 'Blog'
  },
  careers: {
    id: 'careers',
    defaultMessage: 'Careers'
  },
  getHelp: {
    id: 'getHelp',
    defaultMessage: 'Get help'
  },
  partnerships: {
    id: 'partnerships',
    defaultMessage: 'Partnerships'
  },
  contactUs: {
    id: 'contactUs',
    defaultMessage: 'Contact us'
  }
})

interface LinksSectionProps {
  brand: Brand
}
export const FooterLinksSection = ({brand}: LinksSectionProps) => {
  const cookieConsentIsEnabled = checkIsCookieConsentEnabledForBrand(brand)
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)

  return (
    <>
      <LinkSection
        title={formatMessage(messages.productTitle)}
        links={[
          {
            label: formatMessage(messages.affiliateProgram),
            href: brand.links.developersUrl
          },
          cookieConsentIsEnabled && {
            label: formatMessage(messages.cookies),
            onClick: openCookiePreferencesCenter
          },
          {
            label: formatMessage(messages.privacy),
            href: brand.links.privacyPolicyUrl
          },
          {
            label: formatMessage(messages.terms),
            href: brand.links.termsOfUseUrl
          }
        ]}
      />
      <LinkSection
        title={formatMessage(messages.companyTitle)}
        links={[
          {
            label: formatMessage(messages.aboutUs),
            href: brand.links.aboutUsUrl
          },
          {
            label: formatMessage(messages.blog),
            href: brand.links.blogUrl
          },
          {
            label: formatMessage(messages.careers),
            href: brand.links.careersUrl
          }
        ]}
      />
      <LinkSection
        title={formatMessage(messages.contactTitle)}
        links={[
          {
            label: formatMessage(messages.getHelp),
            href: getLocalizedHelpUrl(brand, languageCode)
          },
          {
            label: formatMessage(messages.partnerships),
            href: brand.links.partnershipsUrl
          },
          {
            label: formatMessage(messages.contactUs),
            href: getLocalizedContactUrl(brand, languageCode)
          }
        ]}
      />
    </>
  )
}

interface LinkSectionProps {
  title: string
  links: Array<{label: string; href?: string; onClick?: () => void}>
}
export const LinkSection = ({title, links}: LinkSectionProps) => {
  const validLinks = useMemo(
    () => links.filter(link => link && (link.href || link.onClick)),
    [links]
  )

  if (!validLinks.length) {
    return null
  }
  return (
    <div>
      <Text variant="titleL">{title}</Text>
      <Box paddingTop="s400">
        <UnstyledList>
          {validLinks.map(({label, href, onClick}) => (
            <li key={label}>
              {href ? (
                <StyledLink target="_blank" href={href}>
                  {label}
                </StyledLink>
              ) : (
                <StyledLink onClick={onClick}>{label}</StyledLink>
              )}
            </li>
          ))}
        </UnstyledList>
      </Box>
    </div>
  )
}

const UnstyledList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${cssTheme.layout.spacing.s300};
`

const StyledLink = styled(NoStyleLink)`
  ${cssTheme.typography.bodyM};

  &:hover {
    text-decoration: underline;
  }

  &:active {
    text-decoration: none;
  }
`
